import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StatisticsService } from 'src/app/statistics/services/statistics.service';
import { Color, ScaleType } from '@swimlane/ngx-charts';

@Component({
    selector: 'app-history-chart-dialog',
    templateUrl: './history-chart-dialog.component.html',
    styleUrls: ['./history-chart-dialog.component.scss']
})
export class HistoryChartDialogComponent implements OnInit {
    loading = true;
    error = false;
    chartData: Array<{ name: string, value: number }> = [];
    lineChartData: Array<{ name: string, series: Array<{ name: string, value: number }> }> = [];
    multiChartData: Array<{ name: string, series: Array<{ name: string, value: number }> }> = [];
    yAxisLabel = '';
    isMultiSeries = false;
    isLineChart = false;
    hasValidData = false;
    yAxisTickFormatting = (value: any) => value?.toString() || '0';
    
    // Custom color scheme for line charts
    lineChartColorScheme: Color = {
        domain: ['#009688'],
        group: ScaleType.Linear,
        selectable: false,
        name: 'History Line Chart'
    };

    // Custom color scheme for rate charts (cancellation and no-show rates)
    rateChartColorScheme: Color = {
        domain: ['#f44336'],
        group: ScaleType.Linear,
        selectable: false,
        name: 'Rate Chart'
    };

    constructor(
        public dialogRef: MatDialogRef<HistoryChartDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            groupId: string;
            metric: string;
            dateRange: string;
            currentRange?: { startDate: string; endDate: string }
        },
        private statisticsService: StatisticsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        const rangeType = this.mapDateRangeToRangeType(this.data.dateRange);
        this.yAxisLabel = this.getYAxisLabel(this.data.metric);
        this.isMultiSeries = this.data.metric === 'ageDistribution' || this.data.metric === 'genderBreakdown' || this.data.metric === 'referralSources' || this.data.metric === 'admissionTypes' || this.data.metric === 'lengthOfStayBreakdown';
        this.isLineChart = this.isLineChartMetric(this.data.metric);
        this.loading = true;
        this.error = false;
        this.statisticsService.getHistoricalStatistics(
            this.data.groupId,
            this.data.metric,
            rangeType,
            this.data.currentRange
        ).subscribe({
            next: (res) => {
                if (res.success && Array.isArray(res.data)) {
                    if (this.data.metric === 'ageDistribution' || this.data.metric === 'genderBreakdown' || this.data.metric === 'referralSources' || this.data.metric === 'admissionTypes' || this.data.metric === 'lengthOfStayBreakdown') {
                        this.processMultiSeriesData(res.data);
                        this.hasValidData = this.multiChartData.length > 0 && this.multiChartData.every(series => series.series.length > 0);
                    } else {
                        this.chartData = res.data.map((item: any) => ({ name: item.label, value: item.value ?? 0 }));
                        // Line charts need a different format with series array
                        this.lineChartData = [{
                            name: this.getMetricDisplayName(this.data.metric),
                            series: res.data.map((item: any) => ({ 
                                name: item.label, 
                                value: item.value ?? 0 
                            }))
                        }];
                        this.hasValidData = this.chartData.length > 0;
                    }
                } else {
                    this.error = true;
                    this.hasValidData = false;
                }
                this.loading = false;
                this.cdr.detectChanges();
            },
            error: () => {
                this.error = true;
                this.loading = false;
                this.cdr.detectChanges();
            }
        });
    }

    processMultiSeriesData(data: any[]): void {
        try {
            if (this.data.metric === 'ageDistribution') {
                // Process age distribution data
                const ageGroups = ['0-10', '11-20', '21-30', '31-40', '41-50', '51-60', '61-70', '71-80', '81-90', '91+'];
                this.multiChartData = ageGroups.map(ageGroup => ({
                    name: ageGroup,
                    series: data.map((period: any) => ({
                        name: period.label,
                        value: this.ensureNumber(period.value?.[ageGroup]) || 0
                    }))
                }));
            } else if (this.data.metric === 'genderBreakdown') {
                // Process gender breakdown data - only include genders that have data
                const allGenders = new Set<string>();
                data.forEach(period => {
                    if (period.value && typeof period.value === 'object') {
                        Object.keys(period.value).forEach(gender => allGenders.add(gender));
                    }
                });
                
                const genders = Array.from(allGenders).sort();
                if (genders.length === 0) {
                    genders.push('Unknown');
                }
                
                this.multiChartData = genders.map(gender => ({
                    name: gender,
                    series: data.map((period: any) => ({
                        name: period.label,
                        value: this.ensureNumber(period.value?.[gender]) || 0
                    }))
                }));
            } else if (this.data.metric === 'referralSources') {
                // Process referral sources data - only include sources that have data
                const allReferralSources = new Set<string>();
                data.forEach(period => {
                    if (period.value && typeof period.value === 'object') {
                        Object.keys(period.value).forEach(source => allReferralSources.add(source));
                    }
                });
                
                const referralSources = Array.from(allReferralSources).sort();
                if (referralSources.length === 0) {
                    referralSources.push('No Data');
                }
                
                this.multiChartData = referralSources.map(source => ({
                    name: source,
                    series: data.map((period: any) => ({
                        name: period.label,
                        value: this.ensureNumber(period.value?.[source]) || 0
                    }))
                }));
            } else if (this.data.metric === 'admissionTypes') {
                // Process admission types data - only include admission types that have data
                const allAdmissionTypes = new Set<string>();
                data.forEach(period => {
                    if (period.value && Array.isArray(period.value)) {
                        period.value.forEach((type: any) => {
                            if (type && type.admissionType) {
                                allAdmissionTypes.add(type.admissionType);
                            }
                        });
                    }
                });
                
                const admissionTypes = Array.from(allAdmissionTypes).sort();
                if (admissionTypes.length === 0) {
                    admissionTypes.push('Unknown');
                }
                
                this.multiChartData = admissionTypes.map(admissionType => ({
                    name: admissionType,
                    series: data.map((period: any) => ({
                        name: period.label,
                        value: this.ensureNumber(
                            period.value?.find((type: any) => type?.admissionType === admissionType)?.count
                        ) || 0
                    }))
                }));
            } else if (this.data.metric === 'lengthOfStayBreakdown') {
                // Process length of stay breakdown data - only include categories that have data
                const allLengthOfStayCategories = new Set<string>();
                data.forEach(period => {
                    if (period.value && Array.isArray(period.value)) {
                        period.value.forEach((item: any) => {
                            if (item && item.lengthOfStayCategory) {
                                allLengthOfStayCategories.add(item.lengthOfStayCategory);
                            }
                        });
                    }
                });
                
                const lengthOfStayCategories = Array.from(allLengthOfStayCategories).sort((a, b) => {
                    // Sort by the order they should appear: 1 day, 2-3 days, 4-5 days, etc.
                    const order = ['1 day', '2-3 days', '4-5 days', '6-7 days', '8-14 days', '15-30 days', '> 30 days'];
                    const aIndex = order.indexOf(a);
                    const bIndex = order.indexOf(b);
                    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;
                    return aIndex - bIndex;
                });
                
                if (lengthOfStayCategories.length === 0) {
                    lengthOfStayCategories.push('Unknown');
                }
                
                this.multiChartData = lengthOfStayCategories.map(category => ({
                    name: category,
                    series: data.map((period: any) => ({
                        name: period.label,
                        value: this.ensureNumber(
                            period.value?.find((item: any) => item?.lengthOfStayCategory === category)?.count
                        ) || 0
                    }))
                }));
            }
            
            this.isMultiSeries = true;
        } catch (error) {
            console.error('Error processing multi-series data:', error);
            // Fallback to single series if multi-series fails
            this.fallbackToSingleSeries(data);
        }
    }

    private fallbackToSingleSeries(data: any[]): void {
        const singleSeriesData: Array<{ name: string, value: number }> = [];
        
        if (this.data.metric === 'ageDistribution') {
            const ageGroups = ['0-10', '11-20', '21-30', '31-40', '41-50', '51-60', '61-70', '71-80', '81-90', '91+'];
            data.forEach(period => {
                ageGroups.forEach(ageGroup => {
                    const value = this.ensureNumber(period.value?.[ageGroup]);
                    if (value > 0) {
                        singleSeriesData.push({
                            name: `${period.label} - ${ageGroup}`,
                            value: value
                        });
                    }
                });
            });
        } else if (this.data.metric === 'genderBreakdown') {
            const allGenders = new Set<string>();
            data.forEach(period => {
                if (period.value && typeof period.value === 'object') {
                    Object.keys(period.value).forEach(gender => allGenders.add(gender));
                }
            });
            
            const genders = Array.from(allGenders).sort();
            data.forEach(period => {
                genders.forEach(gender => {
                    const value = this.ensureNumber(period.value?.[gender]);
                    if (value > 0) {
                        singleSeriesData.push({
                            name: `${period.label} - ${gender}`,
                            value: value
                        });
                    }
                });
            });
        } else if (this.data.metric === 'referralSources') {
            const allReferralSources = new Set<string>();
            data.forEach(period => {
                if (period.value && typeof period.value === 'object') {
                    Object.keys(period.value).forEach(source => allReferralSources.add(source));
                }
            });
            
            const referralSources = Array.from(allReferralSources).sort();
            data.forEach(period => {
                referralSources.forEach(source => {
                    const value = this.ensureNumber(period.value?.[source]);
                    if (value > 0) {
                        singleSeriesData.push({
                            name: `${period.label} - ${source}`,
                            value: value
                        });
                    }
                });
            });
        } else if (this.data.metric === 'admissionTypes') {
            data.forEach(period => {
                if (period.value && Array.isArray(period.value)) {
                    period.value.forEach((type: any) => {
                        if (type && type.admissionType) {
                            const value = this.ensureNumber(type.count);
                            if (value > 0) {
                                singleSeriesData.push({
                                    name: `${period.label} - ${type.admissionType}`,
                                    value: value
                                });
                            }
                        }
                    });
                }
            });
        } else if (this.data.metric === 'lengthOfStayBreakdown') {
            data.forEach(period => {
                if (period.value && Array.isArray(period.value)) {
                    period.value.forEach((item: any) => {
                        if (item && item.lengthOfStayCategory) {
                            const value = this.ensureNumber(item.count);
                            if (value > 0) {
                                singleSeriesData.push({
                                    name: `${period.label} - ${item.lengthOfStayCategory}`,
                                    value: value
                                });
                            }
                        }
                    });
                }
            });
        }
        
        this.chartData = singleSeriesData;
        this.isMultiSeries = false;
    }

    private ensureNumber(value: any): number {
        if (value === null || value === undefined) return 0;
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    }

    getYAxisLabel(metric: string): string {
        switch (metric) {
            case 'patientsSeen': return 'Patients Seen';
            case 'meanAge': return 'Mean Age';
            case 'totalScheduledEvents': return 'Total Scheduled Events';
            case 'avgWaitingTime': return 'Average Waiting Time (minutes)';
            case 'totalTimeSpent': return 'Total Time Spent (minutes)';
            case 'totalCancelled': return 'Total Cancelled';
            case 'totalDidNotAttend': return 'Total Did Not Attend';
            case 'cancellationRate': return 'Cancellation Rate (%)';
            case 'noShowRate': return 'No-Show Rate (%)';
            case 'ageDistribution': return 'Number of Patients';
            case 'genderBreakdown': return 'Number of Patients';
            case 'referralSources': return 'Number of Patients';
            case 'totalInpatients': return 'Total Inpatients';
            case 'admissionTypes': return 'Number of Patients';
            case 'totalDischarges': return 'Total Discharges';
            case 'totalReadmissions': return 'Total Readmissions';
            case 'avgLengthOfStay': return 'Average Length of Stay (days)';
            case 'lengthOfStayBreakdown': return 'Number of Patients';
            case 'totalProcedures': return 'Total Procedures';
            case 'onboardingConsentsCompleted': return 'Onboarding Consents Completed';
            case 'onboardingConsentsPending': return 'Onboarding Consents Pending';
            case 'onboardingConsentsCompliance': return 'Onboarding Compliance (%)';
            case 'procedureConsentsCompleted': return 'Procedure Consents Completed';
            case 'procedureConsentsPending': return 'Procedure Consents Pending';
            case 'procedureConsentsCompliance': return 'Procedure Compliance (%)';
            case 'totalInvoices': return 'Total Invoices (ZAR)';
            case 'totalReceipts': return 'Total Receipts (ZAR)';
            case 'totalOutstanding': return 'Total Outstanding (ZAR)';
            case 'morbidityCases': return 'Morbidity Cases';
            case 'mortalityCases': return 'Mortality Cases';
            default: return 'Value';
        }
    }

    mapDateRangeToRangeType(dateRange: string): string {
        switch (dateRange) {
            case 'today': return 'day';
            case 'week': return 'week';
            case 'month': return 'month';
            case 'year': return 'year';
            default: return 'month';
        }
    }

    isLineChartMetric(metric: string): boolean {
        const lineChartMetrics = [
            'patientsSeen',
            'totalScheduledEvents', 
            'avgWaitingTime',
            'totalTimeSpent',
            'totalCancelled',
            'totalDidNotAttend',
            'cancellationRate',
            'noShowRate',
            'totalInpatients',
            'totalDischarges',
            'totalReadmissions',
            'avgLengthOfStay',
            'totalProcedures',
            'onboardingConsentsCompleted',
            'onboardingConsentsPending',
            'onboardingConsentsCompliance',
            'procedureConsentsCompleted',
            'procedureConsentsPending',
            'procedureConsentsCompliance',
            'totalInvoices',
            'totalReceipts',
            'totalOutstanding',
            'morbidityCases',
            'mortalityCases'
        ];
        return lineChartMetrics.includes(metric);
    }

    isRateMetric(metric: string): boolean {
        const rateMetrics = ['cancellationRate', 'noShowRate'];
        return rateMetrics.includes(metric);
    }

    getMetricDisplayName(metric: string): string {
        switch (metric) {
            case 'patientsSeen': return 'Patients Seen';
            case 'totalScheduledEvents': return 'Total Scheduled Events';
            case 'avgWaitingTime': return 'Average Waiting Time';
            case 'totalTimeSpent': return 'Total Time Spent';
            case 'totalCancelled': return 'Total Cancelled';
            case 'totalDidNotAttend': return 'Total Did Not Attend';
            case 'cancellationRate': return 'Cancellation Rate';
            case 'noShowRate': return 'No-Show Rate';
            case 'totalInpatients': return 'Total Inpatients';
            case 'totalDischarges': return 'Total Discharges';
            case 'totalReadmissions': return 'Total Readmissions';
            case 'avgLengthOfStay': return 'Average Length of Stay';
            case 'referralSources': return 'Referral Sources';
            case 'onboardingConsentsCompleted': return 'Onboarding Consents Completed';
            case 'onboardingConsentsPending': return 'Onboarding Consents Pending';
            case 'onboardingConsentsCompliance': return 'Onboarding Compliance';
            case 'procedureConsentsCompleted': return 'Procedure Consents Completed';
            case 'procedureConsentsPending': return 'Procedure Consents Pending';
            case 'procedureConsentsCompliance': return 'Procedure Compliance';
            case 'totalInvoices': return 'Total Invoices';
            case 'totalReceipts': return 'Total Receipts';
            case 'totalOutstanding': return 'Total Outstanding';
            case 'morbidityCases': return 'Morbidity Cases';
            case 'mortalityCases': return 'Mortality Cases';
            default: return 'Value';
        }
    }
} 